import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, GlassCard, GradButton, EmptyState, Field } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import api, { errMsg } from "@/lib/api";
import { PaketDuzenle } from "@/components/PaketDuzenle";

const TUR = { not: "Not", hata: "Hata", soru: "Soru", istek: "İstek" };
const DURUM = [{ value: "acik", label: "Açık" }, { value: "inceleniyor", label: "İnceleniyor" }, { value: "cozuldu", label: "Çözüldü" }];

const Lisans = ({ l, onSaved }) => {
  const [f, setF] = useState(l);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const kaydet = async () => {
    try { await api.put("/satici/lisans", { ...f, finekra_banka_limiti: Number(f.finekra_banka_limiti), kullanici_kotasi: Number(f.kullanici_kotasi) }); toast.success("Abonelik güncellendi"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <GlassCard className="p-6 space-y-5 mb-6" data-testid="satici-lisans">
      <div className="flex flex-wrap justify-between gap-3"><div className="font-medium">Bu firmanın aboneliği</div><GradButton onClick={kaydet} data-testid="satici-kaydet"><Save className="w-4 h-4" />Kaydet</GradButton></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
        <label className="flex items-center gap-2 text-sm pb-2"><Switch data-testid="satici-finekra" checked={f.finekra_aktif} onCheckedChange={(v) => set("finekra_aktif", v)} />Anlık banka hareketleri (Finekra)</label>
        <Field label="Finekra banka sayısı (anlaşmaya göre)"><Input data-testid="satici-banka-limiti" type="number" min="0" className="bg-white/5" value={f.finekra_banka_limiti} onChange={(e) => set("finekra_banka_limiti", e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-sm pb-2"><Switch data-testid="satici-edonusum" checked={f.edonusum_aktif} onCheckedChange={(v) => set("edonusum_aktif", v)} />e-Fatura / e-Arşiv / e-İrsaliye</label>
        <Field label={`Kullanıcı kotası (0 = sınırsız) · aktif ${l.aktif_kullanici}`}><Input data-testid="satici-kota" type="number" min="0" className="bg-white/5" value={f.kullanici_kotasi} onChange={(e) => set("kullanici_kotasi", e.target.value)} /></Field>
      </div>
      <p className="text-xs text-muted-foreground">Bu ayarları yalnızca satıcı paneli yetkilisi görür; firma yöneticisi değiştiremez. Banka sayısını düşürürseniz fazla bağlı hesaplar otomatik olarak ayrılır.</p>
    </GlassCard>
  );
};

const Bildirimler = () => {
  const [list, setList] = useState([]);
  const load = useCallback(async () => setList((await api.get("/satici/geri-bildirimler")).data), []);
  useEffect(() => { load(); }, [load]);
  const durum = async (g, d) => { try { await api.post(`/satici/geri-bildirimler/${g.id}/durum`, { durum: d }); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <GlassCard className="overflow-x-auto" data-testid="satici-bildirimler">
      <div className="px-4 pt-4 pb-2 text-sm font-medium">Notlar ve hata bildirimleri ({list.filter((g) => g.durum !== "cozuldu").length} açık)</div>
      {list.length === 0 ? <EmptyState text="Henüz bildirim yok. Sağ alttaki butondan not ekleyebilirsiniz." testid="satici-bildirim-bos" /> : (
        <table className="w-full text-sm min-w-[720px]"><tbody>{list.map((g) => (
          <tr key={g.id} className={`border-t border-white/5 align-top ${g.durum === "cozuldu" ? "opacity-50" : ""}`} data-testid={`satici-bildirim-${g.id}`}>
            <td className="p-3 text-xs whitespace-nowrap">{new Date(g.created_at).toLocaleString("tr-TR")}<div className="text-muted-foreground">{g.kullanici}</div></td>
            <td className="p-3 text-xs"><span className={g.tur === "hata" ? "text-red-300" : "text-[var(--g1)]"}>{TUR[g.tur]}</span><div className="font-mono text-muted-foreground">{g.sayfa}</div></td>
            <td className="p-3 whitespace-pre-wrap">{g.mesaj}</td>
            <td className="p-3 w-40"><SelectBox testid={`satici-bildirim-durum-${g.id}`} value={g.durum} onChange={(d) => durum(g, d)} options={DURUM} /></td>
          </tr>
        ))}</tbody></table>
      )}
    </GlassCard>
  );
};

const Sifirla = () => {
  const [onay, setOnay] = useState("");
  const sil = async () => {
    try { const { data } = await api.post("/satici/sifirla", { onay }); toast.success(`${data.silinen_kayit} kayıt silindi; kurulum sihirbazı açılıyor`); setTimeout(() => { window.location.href = "/"; }, 1200); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <GlassCard className="p-6 mt-6 border border-red-500/30" data-testid="satici-sifirla">
      <div className="font-medium text-red-200">Firmayı sıfırla</div>
      <p className="text-xs text-muted-foreground mt-1 mb-3">DEMO dahil tüm ürün, cari, sipariş, stok, fatura, banka ve ayar kayıtları silinir; kurulum sihirbazı yeniden açılır. Kullanıcılar, abonelik ve notlar korunur. Geri alınamaz.</p>
      <div className="flex gap-2 max-w-md"><Input data-testid="satici-sifirla-onay" className="bg-white/5" placeholder="Onay için SIFIRLA yazın" value={onay} onChange={(e) => setOnay(e.target.value)} />
        <button disabled={onay !== "SIFIRLA"} onClick={sil} data-testid="satici-sifirla-btn" className="px-5 rounded-full text-sm bg-red-500/80 text-white disabled:opacity-30">Sıfırla</button></div>
    </GlassCard>
  );
};

export default function SaticiPaneli() {
  const [l, setL] = useState(null);
  const load = useCallback(async () => setL((await api.get("/lisans")).data), []);
  useEffect(() => { load(); }, [load]);
  if (!l) return null;
  if (!l.satici) return <GlassCard className="p-8 text-sm" data-testid="satici-yetkisiz">Bu sayfa yalnızca satıcı paneli yetkilisi içindir.</GlassCard>;
  return (
    <div data-testid="satici-page">
      <PageHeader eyebrow="Sistem" title="Satıcı Paneli" desc="Abonelik kapsamı (hizmetler, banka sayısı, kullanıcı kotası) ve deneme sırasında alınan notlar." />
      <Lisans key={JSON.stringify(l)} l={l} onSaved={load} />
      <PaketDuzenle />
      <Bildirimler />
      <Sifirla />
    </div>
  );
}
